import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@common/apis/axios-base-query';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'canceled', 'error']);
const POLL_INTERVAL_MS = 2000;

interface RunTestPayload {
  agentSlug: string;
  textInput: string;
  versionId?: string;
}

export interface AgentRunResult {
  id: string;
  status: string;
  output_data?: {
    content?: string;
    items?: Array<{ data?: string; [key: string]: unknown }>;
    [key: string]: unknown;
  } | null;
  error?: string | null;
  duration_ms?: number;
  _durationMs?: number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const TestApi = createApi({
  reducerPath: 'testApi',
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    runTest: builder.mutation<AgentRunResult, RunTestPayload>({
      queryFn: async ({ agentSlug, textInput, versionId }, _api, _extraOptions, baseQuery) => {
        const startedAt = performance.now();
        const startRes = await baseQuery({
          pathname: `/v1/agents/${encodeURIComponent(agentSlug)}/run`,
          method: 'POST',
          data: { text_input: textInput, ...(versionId ? { version_id: versionId } : {}) },
        });

        if (startRes.error) return { error: startRes.error };

        const runId = (startRes.data as AgentRunResult)?.id;
        if (!runId) {
          return {
            error: { status: 'CUSTOM_ERROR', error: 'No run ID returned from the server.' },
          };
        }

        for (;;) {
          await sleep(POLL_INTERVAL_MS);

          const pollRes = await baseQuery({
            pathname: `/v1/agents/runs/${encodeURIComponent(runId)}`,
            method: 'GET',
          });

          if (pollRes.error) return { error: pollRes.error };

          const run = pollRes.data as AgentRunResult;
          const status = run.status?.toLowerCase() ?? '';

          if (!TERMINAL_STATUSES.has(status)) continue;

          if (status === 'failed' || status === 'error') {
            return {
              error: { status: 'CUSTOM_ERROR', error: run.error ?? 'Agent run failed.' },
            };
          }

          return { data: { ...run, _durationMs: Math.round(performance.now() - startedAt) } };
        }
      },
    }),
  }),
});

export const { useRunTestMutation } = TestApi;
